/**
 * Image Error Handler Plugin for Native Interactive Site Map (ISM)
 *
 * Handles broken image errors in card and detail-panel components by replacing them with a
 * placeholder image. Listens for image load failures and replaces broken images with the
 * placeholder from IRSDomain.
 *
 * Binds a single capture-phase listener on `context.root` rather than on the individual
 * containers that can hold `[data-siteplan-card-image]` images (NHS20-9089/desktop-image
 * regression fix). `error` events don't bubble but do propagate during capture phase through
 * every ancestor, so a listener on the ISM's own root — mounted once for the feature's whole
 * lifetime — keeps working no matter what happens to the containers underneath it. That matters
 * because `[data-secondary-panel]` (desktop's floor-plan carousel container) is created and
 * destroyed on every Floor Plan Configuration tab switch (see
 * components/secondaryPanelController.dom.js / floorPlanDetail.plugin.js's syncSecondaryPanel()):
 * binding directly on it, once, at this plugin's start() — before the user has ever opened that
 * tab — previously left it with no listener at all (querySelector found nothing) or, once
 * recreated, a listener bound to an already-removed node. `[data-panel]` (mobile's equivalent)
 * never gets destroyed, which is why mobile's fallback always worked and desktop's didn't.
 *
 * Lifecycle:
 * - On start: Waits for runtime configuration to be available, then binds the error handler
 * - On stop: Unbinds the error handler and cleans up subscriptions
 *
 * @module nativeInteractiveSiteMap/plugins/imageError.plugin
 */

import { take } from 'rxjs/operators';
import createImageErrorHandler from '../handlers/imageErrorHandler';

export default function createImageErrorPlugin() {
  const state = {
    imageErrorHandler: null,
    subscriptions: [],
  };

  return {
    name: 'image-error-handler',

    async init(context) {
      context.state.imageErrorHandler = {
        started: false,
        ready: false,
      };
    },

    async start(context) {
      if (context.state.imageErrorHandler?.started) {
        return;
      }

      context.state.imageErrorHandler = { started: true, ready: false };

      // Subscribe to configuration$ (emits once when runtime config is available)
      const configurationSubscription = context.services.configuration$
        .pipe(take(1))
        .subscribe(({ imageResizeService }) => {
          const placeholderImageUrl = `${imageResizeService}/images/placeholder.png`;
          state.imageErrorHandler = createImageErrorHandler(context.root, placeholderImageUrl);
          context.state.imageErrorHandler = { started: true, ready: true };
        });

      state.subscriptions.push(configurationSubscription);
    },

    async stop(context) {
      state.imageErrorHandler?.unbind();
      state.imageErrorHandler = null;

      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];

      context.state.imageErrorHandler = { started: false, ready: false };
    },
  };
}
